import React, { Suspense } from 'react';
import {
  Contentheader,
  Footer,
  Header,
  Link,
  PageWrapper,
  Pagesection,
  WebWrapper,
} from '@ktag/styleguide/dist/react';
import Stylesheet from 'components/stylesheet/Stylesheet';
import environment from '@ktag/styleguide/src/javascripts/environment/environment';
import { initReactI18next } from 'react-i18next';
import { i18n } from '@ktag/styleguide/dist/base';
import analytics from '@ktag/styleguide/src/javascripts/analytics/analytics';
import Jobportal from './components/jobportal/Jobportal';
import environmentConfig from './config/environment';

export interface AppProps {
  rootElement?: HTMLDivElement;
  mocked?: boolean;
  standalone?: boolean;
  api?: string;
  title?: string;
  moreLink?: string;
  newThresholdHours?: string;
  preFilter?: { [key: string]: string[] };
  maxEntries?: number;
  filterJobIds?: string[];
  listOnly?: boolean;
  careerUrl?: string;
  volksschuleUrl?: string;
  apprenticeshipUrl?: string;
  translationApiUrl: string;
  translationMissingApiUrl: string;
  privacyUrl?: string;
  loginUrl?: string;
}

interface I18nProjectConfig {
  ns?: string[];
  backend?: { loadPath: string; addPath?: string };
  saveMissing?: boolean;
}

function App({
  rootElement,
  mocked,
  standalone,
  api,
  title,
  moreLink,
  newThresholdHours,
  preFilter,
  maxEntries,
  filterJobIds,
  listOnly,
  careerUrl,
  volksschuleUrl,
  apprenticeshipUrl,
  translationApiUrl,
  translationMissingApiUrl,
  privacyUrl,
  loginUrl,
}: AppProps) {
  // Initialize the environment/analytics with the web-config from @ktag/styleguide
  // if you do NOT pass an environmentConfig, the app will try to use the environment
  // config of the 'surrounding' environment, if it is e.g. embedded in www.ag.ch
  environment.initialize(environmentConfig);
  analytics.initialize();

  // initialize i18n and declare all known required namespaces
  // upfront to combine loading labels into a single request.
  const i18nProjectConfig: I18nProjectConfig = {
    ns: ['core', 'jobs'],
  };
  if (translationApiUrl) {
    i18nProjectConfig.backend = { loadPath: translationApiUrl };
    if (translationMissingApiUrl) {
      i18nProjectConfig.saveMissing = true;
      i18nProjectConfig.backend.addPath = translationMissingApiUrl;
    }
  }
  i18n.initialize(i18nProjectConfig, {
    plugin: initReactI18next,
    allowMultiLoading: true,
  });

  // production view
  if (standalone !== true) {
    return (
      <Suspense fallback={<div />}>
        <WebWrapper renderDocument resetCSS parentElement={rootElement}>
          <Stylesheet inShadowDOM />
          <Jobportal
            mocked={false}
            api={api}
            title={title}
            moreLink={moreLink}
            newThresholdHours={newThresholdHours}
            preFilter={preFilter}
            maxEntries={maxEntries}
            filterJobIds={filterJobIds}
            listOnly={listOnly}
            careerUrl={careerUrl}
            volksschuleUrl={volksschuleUrl}
            apprenticeshipUrl={apprenticeshipUrl}
            privacyUrl={privacyUrl}
            loginUrl={loginUrl}
          />
        </WebWrapper>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div />}>
      <WebWrapper>
        <PageWrapper>
          <Pagesection>
            <Contentheader
              pagetitle="Offene Stellen"
              backlinkText="Offene Stellen"
              printing
            />
          </Pagesection>
          <PageWrapper.Main>
            <Header />
            <Pagesection>
              <Jobportal
                mocked={mocked}
                api={api}
                title={title}
                moreLink={moreLink}
                newThresholdHours={newThresholdHours}
                preFilter={preFilter}
                maxEntries={maxEntries}
                filterJobIds={filterJobIds}
                listOnly={listOnly}
                careerUrl={careerUrl}
                volksschuleUrl={volksschuleUrl}
                apprenticeshipUrl={apprenticeshipUrl}
                privacyUrl={privacyUrl}
                loginUrl={loginUrl}
              />
            </Pagesection>
            {mocked && (
              <Pagesection>
                <Link
                  href={`${process.env.PUBLIC_URL}/mock/umantis/apply.html`}
                  additionalClassNames="jobportallinks__login"
                  icon={{ id: 'external' }}
                  text="DEMO: Bewerbungsmaske"
                />
                <Jobportal
                  mocked={mocked}
                  api={api}
                  title="Offene Stellen"
                  moreLink={moreLink}
                  newThresholdHours={newThresholdHours}
                  preFilter={preFilter}
                  maxEntries={5}
                  filterJobIds={filterJobIds}
                  listOnly
                  careerUrl={careerUrl}
                  volksschuleUrl={volksschuleUrl}
                  apprenticeshipUrl={apprenticeshipUrl}
                  privacyUrl={privacyUrl}
                  loginUrl={loginUrl}
                />
              </Pagesection>
            )}
            <Footer />
          </PageWrapper.Main>
        </PageWrapper>
      </WebWrapper>
    </Suspense>
  );
}

App.defaultProps = {
  standalone: false,
  newThresholdHours: '0',
  preFilter: {},
  maxEntries: 0,
};

export default App;
